/**
 * Pure state helpers for the shared VisibilityPicker (WEB-829 categories,
 * WEB-734 articles). Kept free of React so they can be unit tested.
 *
 *   value = { isPublic: boolean, appIds: number[], roleIds: number[] }
 *
 * category mode: isPublic is an independent switch (signed-out visitors);
 *   appIds map applications (anyone with a role in that app can see it);
 *   roleIds are "group" grants for individual roles.
 * article mode: only roleIds exist. "Everyone" means no roles selected and
 *   the app header toggles every role in that app. isPublic is derived.
 */

export const EVERYONE = Object.freeze({ isPublic: true, appIds: [], roleIds: [] });

const uniq = (ids) => [...new Set(ids)];
const without = (ids, id) => ids.filter((x) => x !== id);
const toggleId = (ids, id) => (ids.includes(id) ? without(ids, id) : [...ids, id]);
const plural = (n, noun) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** Coerce whatever a caller holds into a complete, mode-consistent value. */
export const normalizeValue = (value = {}, mode = 'category') => {
  const roleIds = uniq(value.roleIds || []);
  const appIds = mode === 'article' ? [] : uniq(value.appIds || []);
  const isPublic = mode === 'article' ? roleIds.length === 0 : value.isPublic !== false;
  return { isPublic, appIds, roleIds };
};

/**
 * API category row -> picker value (is_public arrives as 0/1 from MySQL).
 * Returns null when the row does not carry the audience fields (a non-admin
 * response, or an older API). Access settings must fail closed, so callers
 * disable editing rather than defaulting to "Everyone".
 */
export const audienceFromCategory = (category) => {
  if (
    !category ||
    category.is_public === undefined ||
    !Array.isArray(category.app_ids) ||
    !Array.isArray(category.role_ids)
  ) {
    return null;
  }
  return {
    isPublic: category.is_public !== 0 && category.is_public !== false,
    appIds: category.app_ids,
    roleIds: category.role_ids,
  };
};

/** True when the audience is anything other than "Everyone". */
export const isRestricted = (value) => !value.isPublic;

export const isEveryone = (value, mode = 'category') =>
  mode === 'article' ? value.roleIds.length === 0 : value.isPublic;

/** Roles without an application can only be granted individually. */
export const canMapApp = (app) => app != null && app.app_id != null;

/** The "Everyone" row: a switch for categories, "clear all roles" for articles. */
export const togglePublic = (value, mode = 'category') =>
  mode === 'article'
    ? normalizeValue({ ...value, roleIds: [] }, mode)
    : { ...value, isPublic: !value.isPublic };

export const toggleRole = (value, role, mode = 'category') =>
  normalizeValue({ ...value, roleIds: toggleId(value.roleIds, role.role_id) }, mode);

/** Application header: maps the app (category) or toggles all its roles (article). */
export const toggleApp = (value, app, rolesOfApp, mode = 'category') => {
  if (mode === 'category') {
    return canMapApp(app) ? { ...value, appIds: toggleId(value.appIds, app.app_id) } : value;
  }
  const ids = rolesOfApp.map((r) => r.role_id);
  const allSelected = ids.length > 0 && ids.every((id) => value.roleIds.includes(id));
  const others = value.roleIds.filter((id) => !ids.includes(id));
  return normalizeValue({ ...value, roleIds: allSelected ? others : [...others, ...ids] }, mode);
};

/** 'all' | 'some' | 'none' for an application header checkbox. */
export const appCheckState = (value, app, rolesOfApp, mode = 'category') => {
  if (mode === 'category') {
    if (canMapApp(app) && value.appIds.includes(app.app_id)) return 'all';
    return rolesOfApp.some((r) => value.roleIds.includes(r.role_id)) ? 'some' : 'none';
  }
  if (rolesOfApp.length === 0) return 'none';
  const selected = rolesOfApp.filter((r) => value.roleIds.includes(r.role_id)).length;
  if (selected === rolesOfApp.length) return 'all';
  return selected > 0 ? 'some' : 'none';
};

/**
 * In category mode a role is implied (checked, disabled) when its app is
 * mapped. Uses roles.app_id, which is what the API's audience rule reads for
 * a user's applications (user_roles.app_id, falling back to roles.app_id);
 * the two agree whenever role assignments carry their role's own app.
 */
export const isRoleImplied = (value, role, mode = 'category') =>
  mode === 'category' && role.app_id != null && value.appIds.includes(role.app_id);

/**
 * Short human summary for the trigger button and the admin table. Derived
 * from the value first, names second: while the application/role lists are
 * still loading the summary falls back to counts, never to "Admins only".
 */
export const summarize = (value, apps = [], roles = [], mode = 'category') => {
  if (isEveryone(value, mode)) return 'Everyone';

  if (mode === 'article') {
    const selected = roles.filter((r) => value.roleIds.includes(r.role_id));
    if (selected.length === 0) return 'Select roles…';
    return selected.length === 1 ? selected[0].role_name : `${selected.length} roles selected`;
  }

  if (value.appIds.length === 0 && value.roleIds.length === 0) return 'Admins only';

  const parts = [];
  if (value.appIds.length > 0) {
    const names = value.appIds.map((id) => apps.find((a) => a.app_id === id)?.app_name).filter(Boolean);
    parts.push(names.length === value.appIds.length ? names.join(', ') : plural(value.appIds.length, 'application'));
  }

  // Roles already covered by a mapped application are not repeated.
  const grantIds = value.roleIds.filter((id) => {
    const role = roles.find((r) => r.role_id === id);
    return !(role && role.app_id != null && value.appIds.includes(role.app_id));
  });
  if (grantIds.length > 0) {
    const names = grantIds.map((id) => roles.find((r) => r.role_id === id)?.role_name).filter(Boolean);
    parts.push(grantIds.length === 1 && names.length === 1 ? names[0] : plural(grantIds.length, 'role'));
  }
  return parts.join(' + ');
};

/** Admin-table one-liner for a category row; 'unavailable' when the row lacks audience data. */
export const summarizeCategory = (category, apps, roles) => {
  const audience = audienceFromCategory(category);
  return audience ? summarize(audience, apps, roles) : 'unavailable';
};
